import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UserPlus } from 'lucide-react';
import { BottomSheet } from '@shared/ui/containers/BottomSheet';
import { Card } from '@shared/ui/containers/Card';
import { Button } from '@shared/ui/primitives/Button';
import { TextInputField } from '@shared/ui/forms/FormFields';
import { Stepper } from '@shared/ui/lists/Stepper';
import { toast } from '@shared/ui/feedback/Toast';
import { toApiError } from '@shared/api/errors';
import { cn } from '@shared/lib/cn';
import { canPickDeviceContact, pickDeviceContact } from './deviceContactPicker';
import { EntryTypeList } from './EntryTypeList';
import { fetchAccessEntryTypesForInvite, type InviteCaptureRequirements, type MemberAccessEntryTypeOption } from './api';
import {
  MEMBER_CONTACTS_QUERY_KEY,
  createMemberContact,
  updateMemberContact,
  type MemberContactPayload,
  type Person,
} from './memberContactsApi';
import { missingVisitorContact, resolveVisitorContactRule } from './visitorContactRule';
import { ANY_TYPE, displayPhone } from './visitorAccessFormat';

export type PersonDetails = Omit<MemberContactPayload, 'access_entry_type_id'>;

/** Adding a person: what kind of visitor they are, who they are, then a check before saving. Editing stops after Details. */
const STEPS = [
  { label: 'Type', title: 'What kind of visitor?' },
  { label: 'Details', title: '' },
  { label: 'Summary', title: 'Check their details' },
];

/**
 * What Any type asks for: no gate of its own, so the mobile every gate can text, and the rest
 * optional. Each PIN then follows the type picked for it.
 */
const ANY_TYPE_CAPTURE: InviteCaptureRequirements = {
  email: { capture: true, required: false },
  phone: { capture: true, required: true },
  vehicle_reg: { capture: true, required: false },
  headcount: { capture: false, required: false },
};

interface PersonSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The person to edit; adds a new one when left out. */
  person?: Person;
  /** Called once the person is saved. */
  onSaved: (person: Person) => void;
  /** Adding stops after Details, for a booking that makes its own PIN (a guest stay). */
  detailsOnly?: boolean;
}

/**
 * Add or edit one of the member's people, as a stepper: the kind of visitor first, since it decides
 * which details the gate needs, then those details. Adding ends on a summary to check before saving.
 */
export function PersonSheet({ open, onOpenChange, person, onSaved, detailsOnly = false }: PersonSheetProps): JSX.Element {
  // The content mounts with each open, so every open starts on step one with the person as saved.
  return (
    <BottomSheet open={open} onOpenChange={onOpenChange} title={person ? 'Edit details' : 'Add a person'}>
      <PersonSteps person={person} detailsOnly={detailsOnly} onDone={onSaved} />
    </BottomSheet>
  );
}

/** The steps themselves. */
function PersonSteps({
  person,
  detailsOnly,
  onDone,
}: {
  person?: Person;
  detailsOnly: boolean;
  onDone: (person: Person) => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const typesQuery = useQuery({ queryKey: ['access-entry-types'], queryFn: fetchAccessEntryTypesForInvite });
  const [step, setStep] = useState(0);
  // Null is Any type; undefined is none chosen yet.
  const [picked, setPicked] = useState<MemberAccessEntryTypeOption | null>();
  const [details, setDetails] = useState<PersonDetails>();
  const steps = person || detailsOnly ? STEPS.slice(0, 2) : STEPS;

  // 1. The type chosen here, else the one they have. One the member can no longer use counts as none.
  const type =
    picked !== undefined
      ? picked
      : person?.access_entry_type_id === null
        ? null
        : typesQuery.data?.find((row) => row.access_entry_type_id === person?.access_entry_type_id);
  const typeName = type ? type.name : ANY_TYPE.name;

  // 2. Editing saves when Details is done; adding saves from the summary.
  const mutation = useMutation({
    mutationFn: (details: PersonDetails) => {
      const payload = { ...details, access_entry_type_id: type?.access_entry_type_id ?? null };
      return person ? updateMemberContact(person.member_contact_id, payload) : createMemberContact(payload);
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: MEMBER_CONTACTS_QUERY_KEY });
      toast.success(person ? 'Details saved' : `${result.display_name} added`);
      onDone(result);
    },
    onError: (error) => toast.danger("Couldn't save", toApiError(error).message),
  });

  // 3. The summary: each detail with a way back to the step it came from.
  const vehicleAsked = (type?.capture_requirements ?? ANY_TYPE_CAPTURE).vehicle_reg.capture;
  const summary = [
    { label: 'Type', value: typeName, back: 0 },
    { label: 'Full name', value: details?.display_name, back: 1 },
    { label: 'Mobile', value: details?.phone && displayPhone(details.phone), back: 1 },
    { label: 'Email', value: details?.email, back: 1 },
    ...(vehicleAsked ? [{ label: 'Vehicle', value: details?.vehicle_reg, back: 1 }] : []),
  ];

  return (
    <div className="flex flex-col gap-4.5 pb-4">
      {/* 4. Where they are, and a way to any step already open to them. */}
      <Stepper
        total={steps.length}
        current={step}
        labels={steps.map(({ label }) => label)}
        onStepSelect={setStep}
        isStepAccessible={(index) => index < step || (index === 1 && type !== undefined)}
      />
      <div className="flex flex-col gap-1">
        <p className="text-headline font-bold text-ink">{step === 1 ? typeName : steps[step]!.title}</p>
        {step === 0 ? (
          <p className="text-[0.8125rem] leading-snug text-ink-secondary">
            {person
              ? 'Their next PIN follows this. A PIN they already have keeps working until it ends.'
              : 'This decides how their PINs work. You can change it later under Edit details.'}
          </p>
        ) : null}
      </div>

      {/* 5. Type: choosing one moves straight on. */}
      {step === 0 ? (
        <EntryTypeList
          selectedId={type === null ? null : type?.access_entry_type_id}
          mark={person ? 'radio' : 'chevron'}
          onSelect={(choice) => {
            setPicked(choice);
            setStep(1);
          }}
        />
      ) : null}

      {/* 6. Details: only the fields this type captures. Adding goes on to the summary. */}
      {step === 1 && type !== undefined ? (
        <DetailsForm
          initial={details ?? person}
          type={type}
          submitLabel={person || detailsOnly ? 'Save' : 'Continue'}
          saving={mutation.isPending}
          onSubmit={(entered) => {
            if (person || detailsOnly) {
              mutation.mutate(entered);
              return;
            }
            setDetails(entered);
            setStep(2);
          }}
        />
      ) : null}

      {/* 7. Summary, when adding: check, change anything, then add them. */}
      {step === 2 && details ? (
        <>
          <Card noGrain className="flex flex-col divide-y divide-hairline rounded-lg px-3.5 py-0 shadow-none">
            {summary.map(({ label, value, back }) => (
              <div key={label} className="flex items-center justify-between gap-3 py-1.5">
                <span className="flex min-w-0 flex-col gap-0.5 leading-tight">
                  <span className="text-caption text-ink-muted">{label}</span>
                  <span className="truncate text-[0.9375rem] font-semibold text-ink">{value || 'None'}</span>
                </span>
                <button
                  type="button"
                  aria-label={`Change ${label.toLowerCase()}`}
                  onClick={() => setStep(back)}
                  className="min-h-11 px-1 text-sm font-semibold text-primary-500"
                >
                  Change
                </button>
              </div>
            ))}
          </Card>
          <Button width="full" loading={mutation.isPending} onClick={() => mutation.mutate(details)}>
            Add person
          </Button>
        </>
      ) : null}
    </div>
  );
}

interface DetailsFormProps {
  /** The details so far, when there are some. A new person can start from the phone's contacts instead. */
  initial?: PersonDetails;
  /** Decides which fields show and which are required; null is Any type. */
  type: MemberAccessEntryTypeOption | null;
  submitLabel: string;
  saving: boolean;
  onSubmit: (details: PersonDetails) => void;
  /** Puts Cancel beside the submit button, as the Someone new drawer has. */
  onCancel?: () => void;
}

/** Name, mobile, email and vehicle, each shown and required as the person's type asks. */
export function DetailsForm({ initial, type, submitLabel, saving, onSubmit, onCancel }: DetailsFormProps): JSX.Element {
  // 1. Starts from the saved person, if there is one.
  const [name, setName] = useState(initial?.display_name ?? '');
  const [phone, setPhone] = useState(initial?.phone ?? '');
  const [email, setEmail] = useState(initial?.email ?? '');
  const [vehicle, setVehicle] = useState(initial?.vehicle_reg ?? '');
  const [pickedNumbers, setPickedNumbers] = useState<string[]>([]);
  const [tried, setTried] = useState(false);

  // 2. The type decides the fields. A PIN has to reach them, so the rule always leaves a way to send it.
  const capture = type?.capture_requirements ?? ANY_TYPE_CAPTURE;
  const rule = resolveVisitorContactRule(type ?? undefined, capture);
  const missing = missingVisitorContact(rule, normalizePhone(phone), email.trim());
  const problems = {
    name: name.trim() ? '' : 'Enter their name.',
    mobile: missing.mobile ?? '',
    email: missing.email ?? '',
    vehicle: capture.vehicle_reg.required && !vehicle.trim() ? 'Enter their vehicle registration.' : '',
  };
  const shown = (problem: string) => (tried && problem ? problem : undefined);

  const submit = () => {
    if (Object.values(problems).some(Boolean)) {
      setTried(true);
      return;
    }
    onSubmit({
      display_name: name.trim(),
      phone: normalizePhone(phone) || null,
      email: email.trim() || null,
      vehicle_reg: vehicle.trim() || null,
    });
  };

  const fillFromPhoneContacts = async () => {
    // 1. Open the phone's picker. It fails when the member has refused contacts access.
    const picked = await pickDeviceContact().catch(() => null);
    if (!picked) {
      toast.danger("Couldn't open your contacts", 'Allow contacts access in Settings, then try again.');
      return;
    }

    // 2. One number fills the field; several are offered under it to choose from.
    setName(picked.name);
    const [first, ...others] = picked.numbers;
    if (!first) toast.warning('No phone number', 'That contact has no phone number saved.');
    else if (!others.length) setPhone(first);
    setPickedNumbers(others.length ? picked.numbers : []);
  };

  return (
    <div className="flex flex-col gap-3.5">
      {!initial && canPickDeviceContact ? (
        <>
          <Button variant="secondary" onClick={() => void fillFromPhoneContacts()}>
            <UserPlus className="h-4.5 w-4.5 text-primary-600" strokeWidth={2} />
            Pick from phone contacts
          </Button>
          <div className="flex items-center gap-2.5 text-caption text-ink-muted">
            <span className="h-px flex-1 bg-hairline" />
            or type it in
            <span className="h-px flex-1 bg-hairline" />
          </div>
        </>
      ) : null}

      <TextInputField
        label="Full name"
        value={name}
        onChange={(event) => setName(event.target.value)}
        placeholder="Visitor's name"
        error={shown(problems.name)}
      />
      {rule.mobile.show ? (
        <>
          <TextInputField
            label="Mobile"
            optional={!rule.mobile.required && !rule.eitherRequired}
            type="tel"
            inputMode="tel"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            placeholder="+27"
            error={shown(problems.mobile)}
          />
          {pickedNumbers.length ? (
            <div className="-mt-2 flex flex-wrap gap-2">
              {pickedNumbers.map((number) => (
                <button
                  key={number}
                  type="button"
                  onClick={() => {
                    setPhone(number);
                    setPickedNumbers([]);
                  }}
                  className="rounded-pill border border-hairline bg-card px-3 py-1.5 text-body-sm text-ink"
                >
                  {number}
                </button>
              ))}
            </div>
          ) : null}
        </>
      ) : null}
      {rule.email.show ? (
        <TextInputField
          label="Email"
          optional={!rule.email.required && !rule.eitherRequired}
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          error={shown(problems.email)}
        />
      ) : null}
      {capture.vehicle_reg.capture ? (
        <TextInputField
          label="Vehicle registration"
          optional={!capture.vehicle_reg.required}
          value={vehicle}
          placeholder="e.g. CA 123-456"
          onChange={(event) => setVehicle(event.target.value)}
          error={shown(problems.vehicle)}
        />
      ) : null}

      <div className={cn(onCancel && 'grid grid-cols-2 gap-2.5')}>
        {onCancel ? (
          <Button width="full" variant="secondary" disabled={saving} onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
        <Button width="full" loading={saving} onClick={submit}>
          {submitLabel}
        </Button>
      </div>
    </div>
  );
}

/** Store numbers in international form; a local number is taken as South African. */
export function normalizePhone(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (!digits) return '';
  if (value.trim().startsWith('+')) return `+${digits}`;
  if (digits.startsWith('27')) return `+${digits}`;
  return `+27${digits.replace(/^0+/, '')}`;
}
